import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { date, money } from '../../lib/format';
import { LEDGER_LABELS } from '../../lib/labels';
import { sumBy, useInvestorScope } from '../../store/selectors';
import { AboutThisPage } from '../../ui/primitives';
import { investorColors } from '../../ui/theme';
import { Card, SectionLabel, shortEntityName } from '../common';
import TaskList from '../tasks/TaskList';
import ApplicationsPanel from './ApplicationsPanel';
import UpcomingPayments from './UpcomingPayments';

export default function InvestorHomePage() {
  const scope = useInvestorScope();
  const navigate = useNavigate();
  const total = sumBy(scope.holdings, (holding) => holding.value);
  const uncalled = sumBy(scope.holdings, (holding) => holding.uncalled ?? 0);
  const income = sumBy(
    scope.ledger.filter(
      (entry) => entry.type === 'DISTRIBUTION' && entry.effectiveDate >= '2026-07-01',
    ),
    (entry) => entry.amount,
  );
  const openTasks = scope.tasks.filter((task) => task.status === 'OPEN');
  const recent = useMemo(
    () => [...scope.ledger].sort((a, b) => b.seq - a.seq).slice(0, 5),
    [scope.ledger],
  );
  const firstName = scope.login.name.split(' ')[0];

  return (
    <>
      <Box
        sx={{
          bgcolor: investorColors.header,
          color: '#fff',
          borderRadius: '10px',
          p: { xs: 2.5, md: 3.5 },
          mb: 2.5,
        }}
      >
        <Typography variant="body2" component="h1" sx={{ opacity: 0.8 }}>
          Good morning, {firstName}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 800,
            fontSize: { xs: '2rem', md: '2.6rem' },
            mt: 0.5,
          }}
        >
          {money(total)}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Total holdings across {scope.holders.length}{' '}
          {scope.holders.length === 1 ? 'entity' : 'entities'} · from the unit register
        </Typography>
        <Stack
          useFlexGap
          direction="row"
          spacing={4}
          sx={{ mt: 2.5, flexWrap: 'wrap', rowGap: 1.5 }}
        >
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Income this financial year
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>{money(income)}</Typography>
          </Box>
          {uncalled > 0 && (
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Uncalled commitments
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>{money(uncalled)}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              Funds
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {new Set(scope.holdings.map((holding) => holding.fundId)).size}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <AboutThisPage>
        <p>
          Your home screen now reads directly from the fund register, so balances, payments and
          documents are always current. Anything we need from you appears under &quot;Needs your
          attention&quot;.
        </p>
      </AboutThisPage>
      <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' } }}>
        <Box>
          <SectionLabel
            action={
              openTasks.length > 3 ? (
                <Button component={RouterLink} to="/investor/tasks" size="small">
                  All tasks
                </Button>
              ) : undefined
            }
          >
            Needs your attention {openTasks.length > 0 ? `(${openTasks.length})` : ''}
          </SectionLabel>
          <TaskList tasks={openTasks.slice(0, 3)} compact />
          <ApplicationsPanel />
          <SectionLabel>Your entities</SectionLabel>
          <Stack spacing={1.25}>
            {scope.holders.map((holder) => {
              const holdings = scope.holdings.filter((holding) => holding.holderId === holder.id);
              return (
                <Card
                  key={holder.id}
                  onClick={() => navigate(`/investor/portfolio?entity=${holder.id}`)}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }}>{shortEntityName(holder)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {holder.accountNumber} ·{' '}
                        {holdings
                          .map(
                            (holding) =>
                              scope.funds.find((fund) => fund.id === holding.fundId)?.code,
                          )
                          .join(', ') || 'No holdings yet'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontWeight: 700 }}>
                        {money(sumBy(holdings, (holding) => holding.value))}
                      </Typography>
                      <ArrowForwardIcon fontSize="small" color="action" />
                    </Stack>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </Box>
        <Box>
          <SectionLabel>Next 90 days</SectionLabel>
          <UpcomingPayments />
          <SectionLabel
            action={
              <Button component={RouterLink} to="/investor/transactions" size="small">
                All activity
              </Button>
            }
          >
            Recent activity
          </SectionLabel>
          <Card sx={{ p: 0 }}>
            {recent.map((entry, index) => (
              <Stack
                key={entry.id}
                direction="row"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderTop: index ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {LEDGER_LABELS[entry.type]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {scope.funds.find((fund) => fund.id === entry.fundId)?.code} ·{' '}
                    {date(entry.effectiveDate)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {money(entry.amount, true)}
                </Typography>
              </Stack>
            ))}
          </Card>
        </Box>
      </Box>
    </>
  );
}
